import React, { useState, useEffect, useCallback, useRef } from 'react';
import ExportCsvButton from '../components/ExportCsvButton';
import { api } from '../services/authService';
import { useDomain } from '../contexts/DomainContext';

function renderRankChart(points) {
  const W = 620, H = 180, P = 28;
  const vals = points.map((p) => Number(p.position));
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = Math.max(1, max - min);
  const n = points.length;
  const px = (i) => (n === 1 ? W / 2 : P + (i * (W - P * 2)) / (n - 1));
  const py = (v) => P + ((v - min) / span) * (H - P * 2);
  const d = points
    .map((p, i) => (i === 0 ? 'M' : 'L') + px(i).toFixed(1) + ' ' + py(Number(p.position)).toFixed(1))
    .join(' ');
  return (
    <svg viewBox={'0 0 ' + W + ' ' + H} className="w-full h-44">
      <line x1={P} y1={P} x2={W - P} y2={P} stroke="#e5e7eb" />
      <line x1={P} y1={H - P} x2={W - P} y2={H - P} stroke="#e5e7eb" />
      <path d={d} fill="none" stroke="#4f46e5" strokeWidth="2" />
      {points.map((p, i) => (
        <circle key={i} cx={px(i)} cy={py(Number(p.position))} r="3" fill="#4f46e5" />
      ))}
      <text x="2" y={P + 4} fontSize="10" fill="#6b7280">#{min}</text>
      <text x="2" y={H - P + 4} fontSize="10" fill="#6b7280">#{max}</text>
    </svg>
  );
}

// ---------- CSV parsing (supports plain CSV, Google Keyword Planner exports, and Category,Keyword files) ----------

function parseDelimitedLine(line, delim) {
  const out = [];
  let cur = '';
  let q = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (q) {
      if (ch === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++; } else { q = false; }
      } else {
        cur += ch;
      }
    } else {
      if (ch === '"') q = true;
      else if (ch === delim) { out.push(cur); cur = ''; }
      else cur += ch;
    }
  }
  out.push(cur);
  return out.map((c) => c.trim());
}

function parseKeywordFile(text) {
  const clean = String(text || '').replace(/^﻿/, '');
  const lines = clean.split(/\r\n|\n|\r/).filter((l) => l.trim() !== '');
  if (lines.length === 0) return [];

  // Keyword Planner exports are tab-separated; most other files are comma-separated.
  const sample = lines.slice(0, 5).join('\n');
  const delim = sample.indexOf('\t') !== -1 ? '\t' : ',';

  // Find the header row (Keyword Planner has 2 junk lines above it).
  let headerIdx = -1, kwCol = 0, catCol = -1;
  const headerNames = ['keyword', 'keywords', 'keyword text', 'search term', 'search terms', 'query'];
  const catNames = ['category', 'group', 'ad group', 'topic', 'tag'];
  for (let i = 0; i < Math.min(lines.length, 10); i++) {
    const cells = parseDelimitedLine(lines[i], delim).map((c) => c.toLowerCase());
    const ki = cells.findIndex((c) => headerNames.indexOf(c) !== -1);
    if (ki !== -1) {
      headerIdx = i;
      kwCol = ki;
      catCol = cells.findIndex((c) => catNames.indexOf(c) !== -1);
      break;
    }
  }
  // No recognizable header: treat every line's first cell as a keyword.
  const start = headerIdx === -1 ? 0 : headerIdx + 1;

  const rows = [];
  for (let i = start; i < lines.length; i++) {
    const cells = parseDelimitedLine(lines[i], delim);
    const kw = (cells[kwCol] || '').trim();
    if (!kw) continue;
    // Skip Keyword Planner footer/statistic rows
    if (/^(avg\.|total:|statistics|currency|search terms report)/i.test(kw)) continue;
    if (kw.length > 200) continue;
    rows.push({
      keyword: kw,
      category: catCol >= 0 ? (cells[catCol] || '').trim() : ''
    });
  }
  return rows;
}

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => {
      const t = fr.result;
      // Google Keyword Planner CSVs are UTF-16; detect and re-read.
      if (typeof t === 'string' && t.indexOf('\u0000') !== -1) {
        const fr2 = new FileReader();
        fr2.onload = () => resolve(fr2.result);
        fr2.onerror = reject;
        fr2.readAsText(file, 'utf-16le');
      } else {
        resolve(t);
      }
    };
    fr.onerror = reject;
    fr.readAsText(file);
  });
}

export default function RankTracking() {
  const { selectedDomain, domains, selectDomain } = useDomain();
  const domainList = Array.isArray(domains) ? domains : [];
  const currentDomain = selectedDomain || (domainList.length > 0 ? domainList[0].domain : '');

  const [keywords, setKeywords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  // form state
  const [formDomain, setFormDomain] = useState('');
  const [formKeyword, setFormKeyword] = useState('');
  const [formLocation, setFormLocation] = useState('United States');
  const [submitting, setSubmitting] = useState(false);

  // --- rank history ---
  const [historyFor, setHistoryFor] = useState(null);
  const [historyPoints, setHistoryPoints] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [historyDays, setHistoryDays] = useState(90);

  // --- CSV upload / bulk import ---
  const fileInputRef = useRef(null);
  const [uploadRows, setUploadRows] = useState([]);      // rows to import
  const [uploadDupes, setUploadDupes] = useState(0);     // skipped duplicates
  const [uploadFileName, setUploadFileName] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importDone, setImportDone] = useState(0);
  const [importFailed, setImportFailed] = useState(0);
  const importCancelRef = useRef(false);

  // --- table sorting / filtering ---
  const [sortBy, setSortBy] = useState(null);        // null = original order
  const [sortDir, setSortDir] = useState('asc');
  const [searchQ, setSearchQ] = useState('');
  const [tagFilter, setTagFilter] = useState('');

  // The api client in authService already prefixes '/api', so routes here must NOT
  // repeat it. Older builds used '/api/rank-tracking' (-> '/api/api/...', a 404).
  // We resolve the working base once and keep a fallback for safety.
  const [apiBase, setApiBase] = useState('/rank-tracking');
  const apiBaseRef = useRef('/rank-tracking');

  useEffect(() => {
    setFormDomain(currentDomain || '');
  }, [currentDomain]);

  const loadKeywords = useCallback(async () => {
    setLoading(true);
    setError('');
    const bases = [apiBaseRef.current, apiBaseRef.current === '/rank-tracking' ? '/api/rank-tracking' : '/rank-tracking'];
    let loaded = false;
    for (const base of bases) {
      try {
        const res = await api.get(base + '/');
        const all = res?.data?.keywords || res?.data?.data || [];
        setKeywords(Array.isArray(all) ? all : []);
        apiBaseRef.current = base;
        setApiBase(base);
        loaded = true;
        break;
      } catch (e) {
        if (e?.response?.status !== 404) {
          // real error (not a wrong path) — stop trying
          break;
        }
      }
    }
    if (!loaded) setError('Failed to load tracked keywords.');
    setLoading(false);
  }, []);

  useEffect(() => { loadKeywords(); }, [loadKeywords]);

  const domainKeywords = keywords.filter(kw =>
    !kw.domain || (currentDomain && kw.domain && kw.domain.replace(/^www\./, '').toLowerCase() === currentDomain.replace(/^www\./, '').toLowerCase())
  );

  const getRank = (kw) => kw.currentRank ?? kw.current_rank ?? kw.rank ?? kw.currentPosition ?? kw.position ?? null;
  const getPrev = (kw) => kw.previousRank ?? kw.previous_rank ?? kw.previousPosition ?? null;
  const getChange = (kw) => {
    const rank = getRank(kw), prev = getPrev(kw);
    return (rank != null && rank > 0 && prev != null && prev > 0) ? (prev - rank) : null;
  };
  const getTags = (kw) => {
    let t = kw.tags ?? kw.tag ?? null;
    if (typeof t === 'string') { try { t = JSON.parse(t); } catch (e) { t = t ? [t] : []; } }
    return Array.isArray(t) ? t.filter(Boolean).map(String) : [];
  };

  // Numeric accessor per sortable column; null/0-rank values sort last.
  const sortAccessors = {
    keyword: (kw) => String(kw.keyword || '').toLowerCase(),
    position: (kw) => { const r = getRank(kw); return (r != null && r > 0) ? r : null; },
    change: (kw) => getChange(kw),
    volume: (kw) => { const v = kw.searchVolume ?? kw.search_volume; return v != null ? Number(v) : null; },
    difficulty: (kw) => { const v = kw.difficulty; return v != null ? Number(v) : null; },
    cpc: (kw) => { const v = kw.cpc; return (v != null && Number(v) > 0) ? Number(v) : null; }
  };

  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      // Rank reads best ascending (#1 first); metrics read best descending.
      setSortDir(col === 'position' || col === 'keyword' ? 'asc' : 'desc');
    }
  };

  const allTags = Array.from(new Set(domainKeywords.flatMap(getTags))).sort();

  let visibleKeywords = domainKeywords.filter((kw) => {
    if (searchQ && String(kw.keyword || '').toLowerCase().indexOf(searchQ.toLowerCase()) === -1) return false;
    if (tagFilter && getTags(kw).indexOf(tagFilter) === -1) return false;
    return true;
  });
  if (sortBy && sortAccessors[sortBy]) {
    const acc = sortAccessors[sortBy];
    const dir = sortDir === 'asc' ? 1 : -1;
    visibleKeywords = visibleKeywords.slice().sort((a, b) => {
      const va = acc(a), vb = acc(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;   // blanks always last
      if (vb == null) return -1;
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }

  const sortArrow = (col) => sortBy === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
  const thBtn = (col, label, right) => (
    <th className={'py-3 px-5 font-medium' + (right ? ' text-right' : '')}>
      <button type="button" onClick={() => toggleSort(col)}
        className={'hover:text-gray-800 ' + (sortBy === col ? 'text-gray-800 font-semibold' : '')}>
        {label}{sortArrow(col)}
      </button>
    </th>
  );

  const renderFeatures = (raw) => {
    if (!raw) return '-';
    let arr = raw;
    if (typeof raw === 'string') {
      try { arr = JSON.parse(raw); } catch (e) { arr = []; }
    }
    if (!Array.isArray(arr) || arr.length === 0) return '-';
    const label = (x) => String(x).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()).replace(/\bAi\b/g, 'AI').replace(/\bFaq\b/g, 'FAQ').replace(/\bSerp\b/g, 'SERP');
    return (
      <div className="flex flex-wrap gap-1">
        {arr.map((ft, i) => (
          <span key={i} className="inline-block bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded">{label(ft)}</span>
        ))}
      </div>
    );
  };

  const loadHistory = useCallback(async (kw, days) => {
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const res = await api.get(apiBaseRef.current + '/history/' + kw.id + '?days=' + days);
      const pts = (res?.data?.points || []).filter((p) => p && p.position != null);
      setHistoryPoints(pts);
    } catch (err) {
      setHistoryError(err?.response?.data?.error || 'Failed to load ranking history.');
      setHistoryPoints([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const openHistory = (kw) => {
    setHistoryFor(kw);
    setHistoryDays(90);
    setHistoryPoints([]);
    loadHistory(kw, 90);
  };

  const changeHistoryDays = (days) => {
    setHistoryDays(days);
    if (historyFor) loadHistory(historyFor, days);
  };

  const closeHistory = () => {
    setHistoryFor(null);
    setHistoryPoints([]);
    setHistoryError('');
  };

  const addKeywordRequest = (keyword, category) => {
    return api.post(apiBaseRef.current + '/add', {
      keyword: keyword,
      url: formDomain || currentDomain,
      domain: formDomain || currentDomain,
      location: formLocation || 'United States',
      tags: category ? [category] : []
    });
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');
    const kw = formKeyword.trim();
    if (!kw) { setError('Please enter a keyword.'); return; }
    if (!formDomain) { setError('Please select a domain.'); return; }
    setSubmitting(true);
    try {
      await addKeywordRequest(kw, '');
      setNotice('Keyword added. Rankings will populate on the next refresh.');
      setFormKeyword('');
      await loadKeywords();
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to add keyword.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    setError('');
    setNotice('');
    try {
      await api.delete(apiBaseRef.current + '/' + id);
      setKeywords(prev => prev.filter(k => k.id !== id));
    } catch (err) {
      setError('Failed to remove keyword.');
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError('');
    setNotice('');
    try {
      const res = await api.post(apiBaseRef.current + '/refresh', {});
      setNotice(res?.data?.message || 'Rankings refreshed.');
      await loadKeywords();
    } catch (err) {
      setError('Failed to refresh rankings.');
    } finally {
      setRefreshing(false);
    }
  };

  // ---------- CSV upload handlers ----------

  const handleFilePicked = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (e.target) e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setError('');
    setNotice('');
    if (!formDomain && !currentDomain) {
      setError('Please select a domain before uploading keywords.');
      return;
    }
    try {
      const text = await readFileAsText(file);
      const parsed = parseKeywordFile(text);
      if (parsed.length === 0) {
        setError('No keywords found in "' + file.name + '". Expected a CSV with a Keyword column (Google Keyword Planner exports work too).');
        return;
      }
      // Dedupe within the file and against already-tracked keywords for this domain.
      const existing = new Set(domainKeywords.map((k) => String(k.keyword || '').trim().toLowerCase()));
      const seen = new Set();
      const fresh = [];
      let dupes = 0;
      for (const row of parsed) {
        const key = row.keyword.toLowerCase();
        if (existing.has(key) || seen.has(key)) { dupes++; continue; }
        seen.add(key);
        fresh.push(row);
      }
      setUploadRows(fresh);
      setUploadDupes(dupes);
      setUploadFileName(file.name);
      setImportDone(0);
      setImportFailed(0);
      setUploadOpen(true);
      if (fresh.length === 0) {
        // keep modal open to show "everything was a duplicate"
      }
    } catch (err) {
      setError('Could not read "' + file.name + '". Please upload a CSV or text file.');
    }
  };

  const closeUpload = () => {
    if (importing) return; // don't close mid-import
    setUploadOpen(false);
    setUploadRows([]);
    setUploadDupes(0);
    setUploadFileName('');
  };

  const runImport = async () => {
    if (uploadRows.length === 0) return;
    setImporting(true);
    importCancelRef.current = false;
    setImportDone(0);
    setImportFailed(0);
    const CONCURRENCY = 5;
    let done = 0, failed = 0;
    for (let i = 0; i < uploadRows.length && !importCancelRef.current; i += CONCURRENCY) {
      const batch = uploadRows.slice(i, i + CONCURRENCY);
      // eslint-disable-next-line no-await-in-loop
      const results = await Promise.allSettled(batch.map((row) => addKeywordRequest(row.keyword, row.category)));
      results.forEach((r) => { if (r.status === 'rejected') failed++; });
      done += batch.length;
      setImportDone(done);
      setImportFailed(failed);
    }
    setImporting(false);
    setUploadOpen(false);
    const imported = done - failed;
    let msg = 'Imported ' + imported + ' keyword' + (imported === 1 ? '' : 's') + ' from ' + uploadFileName + '.';
    if (uploadDupes > 0) msg += ' ' + uploadDupes + ' duplicate' + (uploadDupes === 1 ? '' : 's') + ' skipped.';
    if (failed > 0) msg += ' ' + failed + ' failed.';
    msg += ' Rankings will populate on the next refresh.';
    setNotice(msg);
    setUploadRows([]);
    setUploadFileName('');
    await loadKeywords();
  };

  const cancelImport = () => { importCancelRef.current = true; };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Rank Tracking</h1>
          <ExportCsvButton data={keywords} filename="rank-tracking" />
          <p className="text-sm text-gray-500">Track keyword positions for your domains.</p>
        </div>
        <button onClick={handleRefresh} disabled={refreshing}
          className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 disabled:opacity-50">
          {refreshing ? 'Refreshing...' : 'Refresh Rankings'}
        </button>
      </div>

      {error && <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}
      {notice && <div className="mb-4 px-4 py-3 rounded-lg bg-green-50 text-green-700 text-sm">{notice}</div>}

      <form onSubmit={handleAdd} className="bg-white border border-gray-200 rounded-xl p-5 mb-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Add a Keyword to Track</h2>
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.txt,.tsv,text/csv,text/plain"
              onChange={handleFilePicked}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              className="px-4 py-2 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 text-sm font-medium hover:bg-blue-100"
            >
              Upload CSV
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div className="md:col-span-1">
            <label className="block text-xs font-medium text-gray-500 mb-1">Domain</label>
            <select value={formDomain} onChange={(e) => { setFormDomain(e.target.value); if (e.target.value && typeof selectDomain === 'function') selectDomain(e.target.value); }}
              className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200">
              {domainList.length === 0 && <option value="">No domains</option>}
              {domainList.map((d) => (
                <option key={d.id || d.domain} value={d.domain}>{d.domain}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-500 mb-1">Keyword</label>
            <input type="text" value={formKeyword} onChange={(e) => setFormKeyword(e.target.value)}
              placeholder="e.g. best running shoes"
              className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200" />
          </div>
          <div className="md:col-span-1">
            <label className="block text-xs font-medium text-gray-500 mb-1">Location</label>
            <input type="text" value={formLocation} onChange={(e) => setFormLocation(e.target.value)}
              placeholder="United States"
              className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200" />
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <button type="submit" disabled={submitting}
            className="px-5 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
            {submitting ? 'Adding...' : 'Add Keyword'}
          </button>
          <span className="text-xs text-gray-400">
            Bulk import: upload a CSV with a Keyword column (Google Keyword Planner exports supported). An optional Category column is saved as a tag.
          </span>
        </div>
      </form>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex flex-wrap items-center gap-3 justify-between">
          <h2 className="text-lg font-semibold text-gray-800">
            Tracked Keywords{currentDomain ? ' for ' + currentDomain : ''}
          </h2>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder="Search keywords..."
              className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200 w-48"
            />
            {allTags.length > 0 && (
              <select
                value={tagFilter}
                onChange={(e) => setTagFilter(e.target.value)}
                className="bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200 max-w-[14rem]"
              >
                <option value="">All categories</option>
                {allTags.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            )}
            {(sortBy || searchQ || tagFilter) && (
              <button type="button"
                onClick={() => { setSortBy(null); setSearchQ(''); setTagFilter(''); }}
                className="text-xs text-gray-400 hover:text-gray-600 underline">
                Reset
              </button>
            )}
            <span className="text-sm text-gray-400">
              {visibleKeywords.length === domainKeywords.length
                ? domainKeywords.length + ' keyword' + (domainKeywords.length === 1 ? '' : 's')
                : visibleKeywords.length + ' of ' + domainKeywords.length}
            </span>
          </div>
        </div>
        {loading ? (
          <div className="px-5 py-10 text-center text-gray-400 text-sm">Loading...</div>
        ) : domainKeywords.length === 0 ? (
          <div className="px-5 py-10 text-center text-gray-400 text-sm">
            No keywords tracked yet for this domain. Add one above or upload a CSV to get started.
          </div>
        ) : visibleKeywords.length === 0 ? (
          <div className="px-5 py-10 text-center text-gray-400 text-sm">
            No keywords match your search or filter.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-100">
                {thBtn('keyword', 'Keyword', false)}
                <th className="py-3 px-5 font-medium">Location</th>
                {thBtn('position', 'Position', true)}
                {thBtn('change', 'Change', true)}
                {thBtn('volume', 'Volume', true)}
                {thBtn('difficulty', 'Difficulty', true)}
                {thBtn('cpc', 'CPC', true)}
                <th className="py-3 px-5 font-medium">SERP Features</th>
                <th className="py-3 px-5 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleKeywords.map((kw) => {
                const rank = getRank(kw);
                const change = getChange(kw);
                return (
                  <tr key={kw.id} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="py-3 px-5 text-gray-800 font-medium">{kw.keyword}</td>
                    <td className="py-3 px-5 text-gray-500">{kw.location || '-'}</td>
                    <td className="py-3 px-5 text-right text-gray-800">{(rank != null && rank > 0) ? rank : '-'}</td>
                    <td className={'py-3 px-5 text-right ' + (change > 0 ? 'text-green-600' : change < 0 ? 'text-red-600' : 'text-gray-400')}>
                      {change == null ? '-' : (change > 0 ? '+' + change : change)}
                    </td>
                    <td className="py-3 px-5 text-right text-gray-500">{kw.searchVolume ?? kw.search_volume ?? '-'}</td>
                    <td className="py-3 px-5 text-right text-gray-500">{(kw.difficulty ?? null) != null ? kw.difficulty : '-'}</td>
                    <td className="py-3 px-5 text-right text-gray-500">{(kw.cpc ?? null) != null && Number(kw.cpc) > 0 ? '$' + Number(kw.cpc).toFixed(2) : '-'}</td>
                    <td className="py-3 px-5">{renderFeatures(kw.serp_features ?? kw.serpFeatures)}</td>
                    <td className="py-3 px-5 text-right">
                      <button onClick={() => openHistory(kw)}
                        className="text-indigo-600 hover:text-indigo-800 text-xs font-medium mr-3">History</button>
                      <button onClick={() => handleDelete(kw.id)}
                        className="text-red-500 hover:text-red-700 text-xs font-medium">Remove</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* CSV import preview / progress modal */}
      {uploadOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4" onClick={closeUpload}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-base font-semibold text-gray-800">Import keywords from CSV</h3>
                <p className="text-xs text-gray-500">{uploadFileName} → {formDomain || currentDomain} ({formLocation || 'United States'})</p>
              </div>
              {!importing && (
                <button onClick={closeUpload} className="text-gray-400 hover:text-gray-600 text-sm">Close</button>
              )}
            </div>

            {importing ? (
              <div className="py-4">
                <p className="text-sm text-gray-700 mb-2">
                  Importing {importDone} / {uploadRows.length}
                  {importFailed > 0 ? ' (' + importFailed + ' failed)' : ''}...
                </p>
                <div className="w-full bg-gray-100 rounded-full h-2 mb-4">
                  <div className="bg-blue-600 h-2 rounded-full transition-all"
                    style={{ width: Math.round((importDone / Math.max(1, uploadRows.length)) * 100) + '%' }} />
                </div>
                <p className="text-xs text-gray-400 mb-3">Keep this page open until the import finishes.</p>
                <button onClick={cancelImport}
                  className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm hover:bg-gray-50">
                  Stop import
                </button>
              </div>
            ) : (
              <div className="py-2">
                <div className="text-sm text-gray-700 mb-3">
                  <p><span className="font-semibold">{uploadRows.length}</span> new keyword{uploadRows.length === 1 ? '' : 's'} ready to import.</p>
                  {uploadDupes > 0 && (
                    <p className="text-gray-500">{uploadDupes} duplicate{uploadDupes === 1 ? '' : 's'} will be skipped (already tracked or repeated in the file).</p>
                  )}
                </div>
                {uploadRows.length > 0 && (
                  <div className="max-h-40 overflow-auto border border-gray-100 rounded-lg mb-4">
                    <table className="w-full text-xs">
                      <tbody>
                        {uploadRows.slice(0, 50).map((row, i) => (
                          <tr key={i} className="border-b border-gray-50">
                            <td className="py-1 px-3 text-gray-700">{row.keyword}</td>
                            <td className="py-1 px-3 text-gray-400 text-right">{row.category || ''}</td>
                          </tr>
                        ))}
                        {uploadRows.length > 50 && (
                          <tr><td className="py-1 px-3 text-gray-400" colSpan="2">...and {uploadRows.length - 50} more</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="flex gap-2">
                  <button onClick={runImport} disabled={uploadRows.length === 0}
                    className="px-5 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
                    Import {uploadRows.length > 0 ? uploadRows.length + ' keyword' + (uploadRows.length === 1 ? '' : 's') : ''}
                  </button>
                  <button onClick={closeUpload}
                    className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm hover:bg-gray-50">
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {historyFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4" onClick={closeHistory}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-base font-semibold text-gray-800">Ranking history</h3>
                <p className="text-xs text-gray-500">{historyFor.keyword} - {historyFor.location || 'United States'}</p>
              </div>
              <button onClick={closeHistory} className="text-gray-400 hover:text-gray-600 text-sm">Close</button>
            </div>
            <div className="flex gap-2 mb-4">
              {[7, 30, 90, 365].map((dv) => (
                <button
                  key={dv}
                  onClick={() => changeHistoryDays(dv)}
                  className={'px-2 py-1 rounded text-xs border ' + (historyDays === dv ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50')}
                >
                  {dv}d
                </button>
              ))}
            </div>
            {historyLoading ? (
              <p className="text-sm text-gray-500 py-10 text-center">Loading history...</p>
            ) : historyError ? (
              <p className="text-sm text-red-600 py-10 text-center">{historyError}</p>
            ) : historyPoints.length === 0 ? (
              <p className="text-sm text-gray-500 py-10 text-center">No ranking checks recorded yet for this keyword.</p>
            ) : (
              <div>
                {renderRankChart(historyPoints)}
                <div className="mt-4 max-h-40 overflow-auto border-t border-gray-100">
                  <table className="w-full text-xs">
                    <tbody>
                      {historyPoints.slice().reverse().map((p, i) => (
                        <tr key={i} className="border-b border-gray-50">
                          <td className="py-1 text-gray-500">{new Date(p.checked_at).toLocaleString()}</td>
                          <td className="py-1 text-right font-medium text-gray-800">#{p.position}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
